import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/AppIcon';
import { NAVIGATION_ITEMS } from '../components/ui/RoleBasedSidebar';

export default function EnterpriseCommandPalette({ userRole = 'user' }) {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const onKeyDown = event => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(current => !current);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }
    requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  const commands = useMemo(
    () =>
      NAVIGATION_ITEMS.flatMap(section =>
        section.items
          .filter(item => item.requiredRole.includes(userRole))
          .map(item => ({ ...item, section: section.section }))
      ),
    [userRole]
  );

  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return commands;
    return commands.filter(item =>
      [item.label, item.tooltip, item.section, item.path]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(term)
    );
  }, [commands, query]);

  const go = path => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex h-9 items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-600 shadow-sm hover:bg-slate-50"
        aria-label="Buscar módulo"
      >
        <Icon name="Search" size={15} />
        <span className="hidden sm:inline">Buscar</span>
        <kbd className="hidden rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-[10px] text-slate-500 lg:inline">
          Ctrl K
        </kbd>
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[200] flex items-start justify-center bg-slate-950/35 px-4 pt-[12vh] backdrop-blur-sm"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setOpen(false);
          }}
        >
          <div
            className="w-full max-w-2xl overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Buscar y navegar"
          >
            <div className="flex items-center gap-3 border-b border-slate-200 px-4">
              <Icon name="Search" size={18} className="text-slate-400" />
              <input
                ref={inputRef}
                value={query}
                onChange={event => setQuery(event.target.value)}
                placeholder="Buscar módulo o acción..."
                className="h-14 flex-1 bg-transparent text-sm text-slate-900 outline-none placeholder:text-slate-400"
              />
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded-md p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <Icon name="X" size={16} />
              </button>
            </div>

            <div className="max-h-[55vh] overflow-y-auto p-2">
              {filtered.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-slate-500">
                  No hay resultados.
                </div>
              ) : (
                filtered.map(item => (
                  <button
                    key={item.path}
                    type="button"
                    onClick={() => go(item.path)}
                    className="flex w-full items-center gap-3 rounded-xl px-3 py-3 text-left hover:bg-slate-50"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600">
                      <Icon name={item.icon} size={17} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-slate-900">
                        {item.label}
                      </span>
                      <span className="block truncate text-xs text-slate-500">
                        {item.section} · {item.tooltip}
                      </span>
                    </span>
                    <Icon
                      name="ArrowRight"
                      size={15}
                      className="text-slate-300"
                    />
                  </button>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
